import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import businessCardApi, { F103, F1031, F1032, F1033 } from '../../../services/businessCardApi';

type Mode = 'browse' | 'add' | 'edit';

/** BR-C01: calculate zodiac from birthday (display only) */
function calculateZodiac(dateStr: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const mmdd = (d.getMonth() + 1) * 100 + d.getDate();
  if (mmdd >= 1222 || mmdd <= 120) return '山羊座';
  if (mmdd <= 219) return '水瓶座';
  if (mmdd <= 320) return '雙魚座';
  if (mmdd <= 420) return '牡羊座';
  if (mmdd <= 521) return '金牛座';
  if (mmdd <= 621) return '雙子座';
  if (mmdd <= 723) return '巨蟹座';
  if (mmdd <= 823) return '獅子座';
  if (mmdd <= 923) return '處女座';
  if (mmdd <= 1023) return '天秤座';
  if (mmdd <= 1122) return '天蠍座';
  return '射手座';
}

const emptyF103: F103 = {
  f103_01: '', f103_11: '', f103_12: '', f103_13: null, f103_14: '',
  f103_15: '', f103_16: '', f103_17: '', f103_18: '', f103_19: '',
  f103_20: '', f103_21: '', f103_22: '', f103_221: '', f103_23: '',
  f103_24: '', f103_25: '', f103_26: '', f103_27: '', f103_28: '',
  f103_29: '', f103_30: 0, f103_31: '', f103_32: '', f103_33: '',
  f103_34: '', f103_35: '', f103_36: '', f103_37: '',
  f103_91: '', f103_92: null, f103_93: '',
  details_1031: [], details_1032: [], details_1033: [],
};

export function useBusinessCardForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F103[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<F103>({ ...emptyF103 });
  const [details1031, setDetails1031] = useState<F1031[]>([]);
  const [details1032, setDetails1032] = useState<F1032[]>([]);
  const [details1033, setDetails1033] = useState<F1033[]>([]);

  const zodiac = calculateZodiac(form.f103_13);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await businessCardApi.list(1, 500);
      setRecords(res.data || []);
      if ((res.data || []).length > 0) {
        setCurrentIdx(0);
      }
    } catch {
      message.error('載入資料失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const syncFields = useCallback((rec: F103) => {
    setForm({ ...emptyF103, ...rec });
    setDetails1031(rec.details_1031 || []);
    setDetails1032(rec.details_1032 || []);
    setDetails1033(rec.details_1033 || []);
  }, []);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  useEffect(() => {
    if (records.length > 0 && currentIdx >= 0 && currentIdx < records.length) {
      // Load full record with sub-tables
      const rec = records[currentIdx];
      if (rec.f103_01) {
        businessCardApi.get(rec.f103_01).then(full => syncFields(full)).catch(() => syncFields(rec));
      } else {
        syncFields(rec);
      }
    }
  }, [records, currentIdx, syncFields]);

  // Navigation
  const navFirst = useCallback(() => setCurrentIdx(0), []);
  const navPrev = useCallback(() => setCurrentIdx((i) => Math.max(0, i - 1)), []);
  const navNext = useCallback(() => setCurrentIdx((i) => Math.min(records.length - 1, i + 1)), [records.length]);
  const navLast = useCallback(() => setCurrentIdx(records.length - 1), [records.length]);

  const setField = useCallback(<K extends keyof F103>(key: K, value: F103[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  }, []);

  const startAdd = useCallback(() => {
    setMode('add');
    setForm({ ...emptyF103 });
    setDetails1031([]);
    setDetails1032([]);
    setDetails1033([]);
  }, []);

  const startEdit = useCallback(() => { setMode('edit'); }, []);

  const cancel = useCallback(() => {
    setMode('browse');
    if (records.length > 0) {
      const rec = records[currentIdx];
      if (rec.f103_01) {
        businessCardApi.get(rec.f103_01).then(full => syncFields(full)).catch(() => syncFields(rec));
      } else {
        syncFields(rec);
      }
    }
  }, [records, currentIdx, syncFields]);

  const save = useCallback(async () => {
    setLoading(true);
    try {
      const payload: Partial<F103> = {
        ...form,
        f103_01: form.f103_01.trim(),
        details_1031: details1031,
        details_1032: details1032,
        details_1033: details1033,
      };
      if (mode === 'add') {
        await businessCardApi.create(payload);
        message.success('新增成功');
      } else {
        await businessCardApi.update(form.f103_01, payload);
        message.success('儲存成功');
      }
      setMode('browse');
      await loadRecords();
    } catch (err: any) {
      if (err?.response?.status === 422) {
        const errors = err.response.data?.errors;
        if (Array.isArray(errors) && errors.length > 0) {
          message.error(errors[0].message);
        }
      } else {
        message.error('儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [mode, form, details1031, details1032, details1033, loadRecords]);

  const doDelete = useCallback(async () => {
    if (!form.f103_01) return;
    setLoading(true);
    try {
      await businessCardApi.delete(form.f103_01);
      message.success('刪除成功');
      await loadRecords();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [form.f103_01, loadRecords]);

  const quickSearch = useCallback((keyword: string) => {
    const idx = records.findIndex((r) =>
      r.f103_01.toUpperCase().includes(keyword.toUpperCase())
    );
    if (idx >= 0) {
      setCurrentIdx(idx);
    } else {
      message.warning('查無資料');
    }
  }, [records]);

  return {
    mode, loading, form, setField, zodiac,
    details1031, setDetails1031,
    details1032, setDetails1032,
    details1033, setDetails1033,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete,
    quickSearch,
    recordCount: records.length,
    recordPosition: records.length > 0 ? currentIdx + 1 : 0,
  };
}
